import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SportPicker from "../components/app/SportPicker";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import LanguageToggle from "../components/app/LanguageToggle";
import Logo from "../components/app/Logo";
import api from "../lib/api";
import {
  ArrowLeft, ArrowRight, User, Users, Trophy, Building2, Check, MapPin,
  Sparkles, Clock, Phone, ShieldCheck, Calendar,
} from "lucide-react";

const ROLES = [
  {
    id: "coach", icon: Trophy,
    title_bg: "Треньор", title_en: "Coach",
    desc_bg: "Индивидуален треньор с profile, booking, календар и клиенти",
    desc_en: "Independent coach with profile, bookings, calendar and clients",
  },
  {
    id: "parent", icon: Users,
    title_bg: "Родител", title_en: "Parent",
    desc_bg: "Управлявай децата си, резервации, чат и прогрес",
    desc_en: "Manage your kids, bookings, chat and progress tracking",
  },
  {
    id: "player", icon: User,
    title_bg: "Играч", title_en: "Player",
    desc_bg: "Собствен профил, статистика, история и комуникация",
    desc_en: "Own profile, stats, training history and coach chat",
  },
  {
    id: "academy", icon: Building2,
    title_bg: "Академия", title_en: "Academy",
    desc_bg: "Множество треньори и играчи, централно управление",
    desc_en: "Multiple coaches and players, central management",
  },
];

const SPECIALTIES = [
  { id: "general", label_bg: "Футболен треньор", label_en: "Football Coach" },
  { id: "goalkeeper", label_bg: "Вратарски", label_en: "Goalkeeper" },
  { id: "fitness", label_bg: "Фитнес", label_en: "Fitness" },
  { id: "technical", label_bg: "Индивидуална техника", label_en: "Technical" },
  { id: "defense", label_bg: "Защита", label_en: "Defense" },
  { id: "attack", label_bg: "Нападение", label_en: "Attack" },
  { id: "youth", label_bg: "Деца", label_en: "Youth" },
];

export default function RegisterWizard() {
  const { user, setUser, formatError } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const presetRole = search.get("role");
  const inviteFromUrl = (search.get("invite") || "").trim().toUpperCase();

  const [step, setStep] = useState(presetRole ? 1 : 0);
  const [form, setForm] = useState({
    role: ["coach", "parent", "player", "academy"].includes(presetRole) ? presetRole : "",
    name: "", email: "", password: "",
    city: "", specialty: "general", hourly_rate: "", available_hours: "", bio: "", years_experience: "",
    position: "", age: "",
    academy_name: "", academy_description: "", academy_size: "small",
    // Parent onboarding — connect/create child profile during registration
    child_name: "", child_age: "", child_position: "",
    invite_code: inviteFromUrl,
    sports: [], zip: "", currency: "USD", is_adult: false,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteError, setInviteError] = useState("");

  useEffect(() => { if (user) nav("/dashboard", { replace: true }); }, [user, nav]);

  // Validate invite code against the public preview endpoint as the user
  // types it (or once on mount when supplied via ?invite=).
  useEffect(() => {
    const code = (form.invite_code || "").trim().toUpperCase();
    setInvitePreview(null);
    setInviteError("");
    if (!code || code.length < 6) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/public/invites/${encodeURIComponent(code)}`);
        if (!cancelled) setInvitePreview(data);
      } catch (e) {
        if (!cancelled) {
          setInvitePreview(null);
          setInviteError(e?.response?.data?.detail || (lang === "bg" ? "Невалиден код" : "Invalid code"));
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.invite_code, lang]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const totalSteps = 4; // role · basic · role-specific · review
  const canNext = () => {
    if (step === 0) return !!form.role;
    if (step === 1) return form.name && form.email && form.password.length >= 6 && (form.role !== "academy" || form.academy_name);
    if (step === 2) return ["coach", "academy"].includes(form.role) ? form.sports.length > 0 : true;
    return true;
  };

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = { ...form, language: lang };
      ["hourly_rate", "years_experience", "age"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      // strip empty strings + role-irrelevant fields
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (payload.role !== "coach") {
        delete payload.specialty; delete payload.hourly_rate; delete payload.available_hours; delete payload.bio; delete payload.years_experience;
      }
      if (payload.role !== "academy") {
        delete payload.academy_name; delete payload.academy_description; delete payload.academy_size;
      }
      if (payload.role !== "player") {
        delete payload.position; delete payload.age; delete payload.is_adult;
      }
      if (!["coach", "academy"].includes(payload.role)) { delete payload.sports; delete payload.zip; }
      if (payload.role !== "coach") delete payload.currency;
      // Parent-only child fields — never sent to /auth/register (handled
      // in a follow-up /children call below).
      const childOnboarding = payload.role === "parent" && (form.child_name?.trim() || form.child_age) ? {
        name: form.child_name?.trim() || "",
        age: form.child_age ? Number(form.child_age) : null,
        position: form.child_position?.trim() || undefined,
      } : null;
      delete payload.child_name; delete payload.child_age; delete payload.child_position;

      const { data } = await api.post("/auth/register", payload);
      // /auth/register returns {user, token} and sets httpOnly cookie + we store the Bearer for fallback
      if (data?.token) {
        try { localStorage.setItem("auth_token", data.token); } catch (_) {}
      }
      // Best-effort: link/create the child profile right after the parent
      // account is created. Silent fail — parent can add later from /children.
      if (childOnboarding && childOnboarding.name) {
        try {
          await api.post("/children", {
            name: childOnboarding.name,
            age: childOnboarding.age ?? 8,
            position: childOnboarding.position,
          });
        } catch (_) {}
      }
      if (setUser && data?.user) setUser(data.user);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatError ? formatError(e2) : (e2?.response?.data?.detail || "Error"));
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white">
      {/* Header */}
      <header className="header-blur">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/" className="flex items-center"><Logo size={26} wordmark /></Link>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors" data-testid="wizard-login-link">
              {t("login")}
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 grid place-items-center px-4 py-10">
        <div className="w-full max-w-3xl">
          {/* Progress bar */}
          <div className="flex items-center gap-2 mb-8">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div key={i} className={`h-1 flex-1 transition-colors duration-500 ${i <= step ? "bg-pitch" : "bg-white/8"}`} />
            ))}
          </div>
          <div className="flex items-center justify-between mb-6">
            <div className="label-mono">
              {lang === "bg" ? `Стъпка ${step + 1} / ${totalSteps}` : `Step ${step + 1} / ${totalSteps}`}
            </div>
            <div className="text-[10px] font-mono text-white/35 uppercase tracking-widest">
              {form.role && form.role.toUpperCase()}
            </div>
          </div>

          {/* Invite banner — shown when ?invite= present or user has entered code */}
          {invitePreview?.kind === "recruit" && (
            <div className="surface-elevated p-4 mb-5 border-pitch/50 bg-pitch/[0.04] flex items-start gap-3" data-testid="recruit-banner">
              <div className="w-10 h-10 rounded-full bg-pitch text-white flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="label-mono text-pitch">{lang === "bg" ? "Лична покана" : "Personal invitation"}</div>
                <div className="font-oswald uppercase text-lg tracking-tight mt-0.5">{lang === "bg" ? "Стани треньор в ONTHEPITCH — Лос Анджелис" : "Coach on ONTHEPITCH — Los Angeles"}</div>
                <div className="text-xs text-white/60 mt-1">{lang === "bg" ? "Регистрацията е безплатна. Профилът ти се преглежда от нашия екип преди да стане публичен." : "Registration is free. Our team reviews your profile before it goes public."}</div>
              </div>
            </div>
          )}
          {invitePreview && invitePreview.kind !== "recruit" && (
            <div className="surface-elevated p-4 mb-5 border-pitch/50 bg-pitch/[0.04] flex items-start gap-3 animate-fade-up" style={{ opacity: 0 }} data-testid="invite-banner-active">
              <div className="w-10 h-10 rounded-full bg-pitch text-white flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-mono text-pitch">// {lang === "bg" ? "Поканен си с код" : "You're invited"}</div>
                <div className="font-oswald uppercase text-lg tracking-tight mt-0.5">
                  {invitePreview.pro_duration_days
                    ? (lang === "bg" ? `Безплатен Pro достъп за ${invitePreview.pro_duration_days} дни` : `Free Pro access for ${invitePreview.pro_duration_days} days`)
                    : (lang === "bg" ? "Доживотен безплатен Pro достъп" : "Lifetime free Pro access")}
                </div>
                <div className="text-[11px] text-white/55 font-mono mt-1">
                  {lang === "bg" ? "Код: " : "Code: "}<span className="text-pitch">{form.invite_code}</span>
                  {invitePreview.role_hint && (
                    <>
                      {" · "}
                      <span className={form.role && form.role !== invitePreview.role_hint ? "text-red-400" : "text-pitch"}>
                        {lang === "bg" ? "За: " : "For: "}{invitePreview.role_hint.toUpperCase()}
                      </span>
                    </>
                  )}
                </div>
                {/* Role mismatch warning — invite is reserved for a specific role */}
                {invitePreview.role_hint && form.role && form.role !== invitePreview.role_hint && (
                  <div className="text-[11px] text-red-400 font-mono mt-2 flex items-start gap-1.5" data-testid="invite-role-mismatch">
                    <span>⚠</span>
                    <span>
                      {lang === "bg"
                        ? `Този код е само за ${invitePreview.role_hint} акаунти. Смени ролята си или махни кода.`
                        : `This code is reserved for ${invitePreview.role_hint} accounts. Switch role or remove the code.`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {inviteError && form.invite_code && (
            <div className="surface-1 p-3 mb-5 border border-red-500/30 text-xs font-mono text-red-300" data-testid="invite-banner-error">
              {inviteError}
            </div>
          )}

          <div className="surface-elevated p-6 sm:p-10 min-h-[420px] flex flex-col">
            {/* STEP 0 — ROLE */}
            {step === 0 && (
              <div className="animate-fade-up" style={{ opacity: 0 }}>
                <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
                  {lang === "bg" ? "Кой си ти?" : "Who are you?"}
                </h1>
                <p className="text-sm text-white/55 mt-2">
                  {lang === "bg" ? "Избери тип акаунт. Може да го промениш по-късно от настройките." : "Pick an account type. You can change it later from settings."}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                  {ROLES.map((r) => {
                    const active = form.role === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setF("role", r.id)}
                        className={`text-left p-5 border transition-all duration-300 group relative overflow-hidden ${
                          active ? "border-pitch bg-pitch/5" : "border-white/10 hover:border-pitch/50"
                        }`}
                        data-testid={`role-card-${r.id}`}
                      >
                        {active && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-pitch text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${active ? "bg-pitch text-white" : "bg-white/5 text-pitch"}`}>
                          <r.icon className="w-5 h-5" />
                        </div>
                        <div className="font-oswald uppercase text-xl tracking-tight mt-3">{lang === "bg" ? r.title_bg : r.title_en}</div>
                        <div className="text-xs text-white/55 mt-1 font-mono leading-relaxed">{lang === "bg" ? r.desc_bg : r.desc_en}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 1 — BASIC INFO */}
            {step === 1 && (
              <div className="animate-fade-up" style={{ opacity: 0 }}>
                <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
                  {lang === "bg" ? "Основни данни" : "Account basics"}
                </h1>
                <p className="text-sm text-white/55 mt-2">
                  {lang === "bg" ? "Само имейл и парола. Останалото — на следваща стъпка." : "Just an email and password. Everything else comes next."}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                  <WInput label={form.role === "academy" ? (lang === "bg" ? "Твоето име" : "Your name") : t("name")} v={form.name} on={(v) => setF("name", v)} testId="reg-name" />
                  <WInput label={t("email")} type="email" v={form.email} on={(v) => setF("email", v)} testId="reg-email" />
                  <WInput label={t("password")} type="password" v={form.password} on={(v) => setF("password", v)} testId="reg-password" />
                  {form.role === "academy" && (
                    <WInput label={lang === "bg" ? "Име на академията" : "Academy name"} v={form.academy_name} on={(v) => setF("academy_name", v)} testId="reg-academy-name" icon={Building2} />
                  )}
                </div>
                {form.password && form.password.length < 6 && (
                  <div className="text-xs text-amber-400 font-mono mt-3">{lang === "bg" ? "Минимум 6 символа" : "At least 6 characters"}</div>
                )}
                {/* Optional invite / referral code */}
                <div className="mt-6 pt-5 border-t border-white/8">
                  <details className="group" open={!!form.invite_code}>
                    <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest text-white/45 hover:text-pitch transition-colors flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" />
                      {lang === "bg" ? "Имам код за покана (по избор)" : "I have an invite code (optional)"}
                    </summary>
                    <div className="mt-3">
                      <WInput
                        label={lang === "bg" ? "Код за покана" : "Invite code"}
                        v={form.invite_code}
                        on={(v) => setF("invite_code", (v || "").toUpperCase())}
                        testId="reg-invite-code"
                        placeholder="JOIN-XXX0000"
                      />
                    </div>
                  </details>
                </div>
              </div>
            )}

            {/* STEP 2 — ROLE-SPECIFIC */}
            {step === 2 && (
              <div className="animate-fade-up" style={{ opacity: 0 }}>
                <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
                  {lang === "bg" ? "Кажи ни малко за теб" : "Tell us a bit about you"}
                </h1>
                <p className="text-sm text-white/55 mt-2">
                  {lang === "bg" ? "Незадължително — може да допълниш по-късно от Настройки." : "Optional — you can complete this later in Settings."}
                </p>

                {form.role === "coach" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                    <div className="sm:col-span-2">
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Спортове (поне един)" : "Sports you coach (at least one)"}</label>
                      <SportPicker value={form.sports} onChange={(v) => setF("sports", v)} testid="reg-sports" />
                    </div>
                    <WInput label={lang === "bg" ? "Град" : "City"} v={form.city} on={(v) => setF("city", v)} testId="reg-city" icon={MapPin} />
                    <WInput label={lang === "bg" ? "Пощенски код" : "ZIP code"} v={form.zip} on={(v) => setF("zip", v)} testId="reg-zip" icon={MapPin} />
                    <div>
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Валута" : "Currency"}</label>
                      <select value={form.currency} onChange={(e) => setF("currency", e.target.value)} className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch" data-testid="reg-currency">
                        <option value="USD">USD $</option><option value="EUR">EUR €</option>
                      </select>
                    </div>
                    <WInput label={lang === "bg" ? "Цена на час" : "Hourly rate"} type="number" v={form.hourly_rate} on={(v) => setF("hourly_rate", v)} testId="reg-rate" icon={Sparkles} />
                    <WInput label={lang === "bg" ? "Опит (г.)" : "Experience (yr)"} type="number" v={form.years_experience} on={(v) => setF("years_experience", v)} testId="reg-exp" icon={Trophy} />
                    <WInput label={lang === "bg" ? "Налични часове" : "Available hours"} v={form.available_hours} on={(v) => setF("available_hours", v)} testId="reg-hours" placeholder="Mon-Fri 16:00–20:00" icon={Clock} className="sm:col-span-2" />
                    <div className="sm:col-span-2">
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Кратка биография" : "Bio"}</label>
                      <textarea rows={3} value={form.bio} onChange={(e) => setF("bio", e.target.value)} className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch" data-testid="reg-bio" />
                    </div>
                  </div>
                )}

                {form.role === "parent" && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                      <WInput label={lang === "bg" ? "Град" : "City"} v={form.city} on={(v) => setF("city", v)} testId="reg-city" icon={MapPin} />
                      <WInput label={lang === "bg" ? "Телефон (по избор)" : "Phone (optional)"} v={form.phone} on={(v) => setF("phone", v)} testId="reg-phone" icon={Phone} />
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/8">
                      <div className="label-mono mb-2">// {lang === "bg" ? "Твоят играч" : "Your player"}</div>
                      <p className="text-xs text-white/45 font-mono mb-4">
                        {lang === "bg"
                          ? "Свържи профила си с детето си веднага — то ще се появи в твоите сесии и резервации."
                          : "Link your account to your player right away — they'll show up in your sessions and bookings."}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <WInput label={lang === "bg" ? "Име на детето" : "Player name"} v={form.child_name} on={(v) => setF("child_name", v)} testId="reg-child-name" className="sm:col-span-2" />
                        <WInput label={lang === "bg" ? "Възраст" : "Age"} type="number" v={form.child_age} on={(v) => setF("child_age", v)} testId="reg-child-age" />
                        <WInput label={lang === "bg" ? "Позиция (по избор)" : "Position (optional)"} v={form.child_position} on={(v) => setF("child_position", v)} testId="reg-child-position" placeholder={lang === "bg" ? "напр. Нападател" : "e.g. Striker"} className="sm:col-span-3" />
                      </div>
                      <div className="text-[10px] text-white/30 font-mono mt-2">
                        {lang === "bg" ? "По желание — можеш да добавиш по-късно." : "Optional — you can add this later."}
                      </div>
                    </div>
                  </>
                )}

                {form.role === "player" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                    <WInput label={lang === "bg" ? "Позиция" : "Position"} v={form.position} on={(v) => setF("position", v)} testId="reg-position" placeholder={lang === "bg" ? "напр. Нападател" : "e.g. Striker"} />
                    <WInput label={lang === "bg" ? "Възраст" : "Age"} type="number" v={form.age} on={(v) => setF("age", v)} testId="reg-age" />
                    <WInput label={lang === "bg" ? "Град" : "City"} v={form.city} on={(v) => setF("city", v)} testId="reg-city" icon={MapPin} className="sm:col-span-2" />
                    <label className="sm:col-span-2 flex items-start gap-3 text-sm text-white/80 cursor-pointer">
                      <input type="checkbox" checked={form.is_adult} onChange={(e) => setF("is_adult", e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#3B82F6]" data-testid="reg-is-adult" />
                      {lang === "bg" ? "Навършил/а съм 18 години и ще резервирам и плащам сам/а. (Под 18 — родител резервира за теб.)" : "I am 18 or older and will book and pay for myself. (Under 18? A parent books for you.)"}
                    </label>
                  </div>
                )}

                {form.role === "academy" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7">
                    <div className="sm:col-span-2">
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Спортове (поне един)" : "Sports you coach (at least one)"}</label>
                      <SportPicker value={form.sports} onChange={(v) => setF("sports", v)} testid="reg-sports" />
                    </div>

                    <WInput label={lang === "bg" ? "Град" : "City"} v={form.city} on={(v) => setF("city", v)} testId="reg-city" icon={MapPin} />
                    <div>
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Размер" : "Size"}</label>
                      <select value={form.academy_size} onChange={(e) => setF("academy_size", e.target.value)} className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch" data-testid="reg-academy-size">
                        <option value="small">{lang === "bg" ? "Малка (≤50 деца)" : "Small (≤50 kids)"}</option>
                        <option value="medium">{lang === "bg" ? "Средна (50–200)" : "Medium (50–200)"}</option>
                        <option value="large">{lang === "bg" ? "Голяма (200+)" : "Large (200+)"}</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label-mono block mb-1.5">{lang === "bg" ? "Описание" : "Description"}</label>
                      <textarea rows={3} value={form.academy_description} onChange={(e) => setF("academy_description", e.target.value)} className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch" data-testid="reg-academy-desc" placeholder={lang === "bg" ? "Кратко за вашата академия..." : "Brief about your academy..."} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — REVIEW */}
            {step === 3 && (
              <div className="animate-fade-up" style={{ opacity: 0 }}>
                <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
                  {lang === "bg" ? "Всичко готово?" : "All set?"}
                </h1>
                <p className="text-sm text-white/55 mt-2">
                  {lang === "bg" ? "Прегледай данните си преди да създадем акаунта." : "Review your details before we create your account."}
                </p>
                <div className="mt-7 space-y-2 text-sm font-mono">
                  <Row k={lang === "bg" ? "Роля" : "Role"} v={form.role.toUpperCase()} />
                  <Row k={lang === "bg" ? "Име" : "Name"} v={form.name} />
                  <Row k={t("email")} v={form.email} />
                  {form.role === "academy" && <Row k={lang === "bg" ? "Академия" : "Academy"} v={form.academy_name} />}
                  {form.city && <Row k={lang === "bg" ? "Град" : "City"} v={form.city} />}
                  {form.specialty && form.role === "coach" && <Row k={lang === "bg" ? "Специалност" : "Specialty"} v={form.specialty} />}
                  {form.hourly_rate && form.role === "coach" && <Row k={lang === "bg" ? "Цена" : "Rate"} v={`€${form.hourly_rate}/h`} />}
                </div>
                {err && <div className="text-red-400 text-sm font-mono mt-4" data-testid="wizard-error">{err}</div>}
                <p className="text-[11px] text-white/35 mt-6 font-mono leading-relaxed">
                  {lang === "bg"
                    ? "Натискайки „Завърши“ потвърждаваш Условия и Privacy Policy."
                    : "By tapping „Finish“ you accept our Terms and Privacy Policy."}
                </p>
              </div>
            )}

            {/* Nav buttons */}
            <div className="mt-auto pt-8 flex items-center justify-between">
              <button
                type="button"
                onClick={() => (step === 0 ? nav("/") : setStep((s) => s - 1))}
                className="text-xs font-mono uppercase tracking-widest text-white/55 hover:text-white flex items-center gap-1"
                data-testid="wizard-back"
              >
                <ArrowLeft className="w-3 h-3" /> {step === 0 ? (lang === "bg" ? "Към начало" : "Home") : (lang === "bg" ? "Назад" : "Back")}
              </button>
              {step < totalSteps - 1 ? (
                <button
                  type="button"
                  onClick={() => canNext() && setStep((s) => s + 1)}
                  disabled={!canNext()}
                  className="btn-primary px-5 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="wizard-next"
                >
                  {lang === "bg" ? "Напред" : "Next"} <ArrowRight className="w-3 h-3" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  className="btn-primary px-6 py-3 text-xs flex items-center gap-2 glow-emerald-soft"
                  data-testid="wizard-submit"
                >
                  <Sparkles className="w-4 h-4" /> {busy ? "..." : (lang === "bg" ? "Завърши" : "Finish")}
                </button>
              )}
            </div>
          </div>

          <p className="text-center text-xs text-white/35 font-mono mt-5">
            {t("haveAccount")} <Link to="/login" className="text-pitch hover:underline" data-testid="link-to-login">{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function WInput({ label, v, on, type = "text", testId, icon: Icon, placeholder, className = "" }) {
  return (
    <div className={className}>
      <label className="label-mono block mb-1.5">{label}</label>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />}
        <input
          type={type}
          value={v ?? ""}
          placeholder={placeholder}
          onChange={(e) => on(e.target.value)}
          className={`w-full bg-[#0A1628] border border-white/10 ${Icon ? "pl-9" : "px-3"} pr-3 py-2.5 outline-none focus:border-pitch transition-colors`}
          data-testid={testId}
        />
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/5 py-2">
      <span className="text-white/45 uppercase tracking-widest text-[10px]">{k}</span>
      <span className="text-white font-mono text-right truncate max-w-[60%]">{v || "—"}</span>
    </div>
  );
}
